import { useCallback } from 'react';

import { useAppDispatch } from '~/app/store';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import useToasts from '~/common/components/toasts/useToasts';

import { getPresentationGroupKey } from './batch.utils';
import { getPresentationHandlers } from './presentationHandlers';
import { getRetryIds, isBatchRetryable } from './retry.utils';
import type { ActivationBatch } from './state/activationProgressSlice';
import {
  createActivationBatch,
  markActivationCompleted,
  replaceRetriedBatches,
} from './state/activationProgressSlice';
import { useRetryActivatePeripherals } from './useRetryActivatePeripherals';
import { useRetryingBatchIds } from './useRetryingBatchIds';

const RETRY_ERROR_KEY = 'catalog:order.activationProgress.error';

/**
 * Result of retrying a single terminal batch:
 * - `error`: the retry failed to start (surface a toast, keep the card).
 * - `sync`: succeeded with no batch to poll — retire the card, refresh lists.
 * - `batch`: succeeded with a new batch — retire the card, track the new batch.
 */
type RetryOutcome =
  | { kind: 'error' }
  | { kind: 'sync'; retiredBatchId: string }
  | { kind: 'batch'; retiredBatchId: string; newBatch: ActivationBatch };

export type UseRetryActivationBatchResult = {
  /**
   * Re-runs activation for the failed devices of the given terminal batches and
   * registers the returned batch(es) so the progress widget resumes tracking.
   * Targets each batch's `failedPeripheralIds` when known, falling back to its
   * original `peripheralIds` for a wholesale failure. Surfaces an error toast if
   * any retry request fails to start. Batches with a custom handler registered
   * for their presentation group are ignored — the snackbar invokes that handler.
   */
  retry: (batches: ActivationBatch[]) => Promise<void>;
  /** True while any retry request is in flight. */
  isRetrying: boolean;
  /**
   * Ids of the batches currently being retried. A multi-card snackbar scopes
   * `isRetrying` to just the card whose own batches are in this list, so
   * retrying one presentation group's card never disables or blocks closing
   * an unrelated group's.
   */
  retryingBatchIds: string[];
};

/**
 * Powers the completion card's "try again" action for the built-in peripheral
 * retry-activate flow: retries the failed devices of finished bulk activation
 * batches and hands the new batch back to the global progress widget for
 * polling. Batches with a registered custom retry handler are skipped here —
 * the snackbar calls that instead (see
 * {@link useRetryingBatchIds}, shared by both mechanisms for the in-flight
 * bookkeeping).
 */
export const useRetryActivationBatch = (): UseRetryActivationBatchResult => {
  const dispatch = useAppDispatch();
  const { submit } = useRetryActivatePeripherals();
  const { showErrorToast } = useToasts();
  const { retryingBatchIds, track } = useRetryingBatchIds();

  const retry = useCallback(
    async (batches: ActivationBatch[]): Promise<void> => {
      const retryable = batches.filter(
        (batch) =>
          isBatchRetryable(batch) &&
          !getPresentationHandlers(getPresentationGroupKey(batch))
            ?.onRetryBatches
      );
      if (retryable.length === 0) return;

      const retryingIds = retryable.map((batch) => batch.batchId);
      await track(retryingIds, async () => {
        const outcomes = await Promise.all(
          retryable.map(async (batch): Promise<RetryOutcome> => {
            const { peripheralType } = batch;
            // Guaranteed by the `retryable` filter; guard keeps types honest.
            if (!peripheralType) return { kind: 'error' };
            const peripheralIds = getRetryIds(batch);
            const outcome = await submit({
              orgId: batch.orgId,
              fleetId: batch.fleetId,
              peripheralIds,
              peripheralType,
            });

            if (outcome.status === 'error') {
              return { kind: 'error' };
            }

            // Success with no batch to poll (synchronous activation): retire the
            // terminal card and refresh device lists instead of tracking a batch,
            // mirroring the row-level retry (useRetryPeripheral).
            if (!outcome.batchId) {
              return { kind: 'sync', retiredBatchId: batch.batchId };
            }

            return {
              kind: 'batch',
              retiredBatchId: batch.batchId,
              newBatch: createActivationBatch({
                batchId: outcome.batchId,
                orgId: batch.orgId,
                productName: batch.productName,
                // Carried over so the retried batch stays on the original
                // batch's card instead of falling into the default group.
                presentationGroup: batch.presentationGroup,
                progressTitle: batch.progressTitle,
                completeTitle: batch.completeTitle,
                primaryActionLabel: batch.primaryActionLabel,
                peripheralType,
                fleetId: batch.fleetId,
                peripheralIds,
                total: peripheralIds.length,
              }),
            };
          })
        );

        // Retire every batch whose retry started (sync or async) in one atomic
        // swap, registering new in-progress batches only for the async ones, so
        // a failing sibling retry never has its terminal batch cleared out from
        // under it.
        const started = outcomes.filter(
          (outcome): outcome is Exclude<RetryOutcome, { kind: 'error' }> =>
            outcome.kind !== 'error'
        );
        if (started.length > 0) {
          dispatch(
            replaceRetriedBatches({
              retiredBatchIds: started.map((o) => o.retiredBatchId),
              newBatches: started
                .filter(
                  (o): o is Extract<RetryOutcome, { kind: 'batch' }> =>
                    o.kind === 'batch'
                )
                .map((o) => o.newBatch),
            })
          );
        }

        // Sync successes have no batch to poll, so nudge the device lists to
        // refetch (the peripherals API has no cache tags to invalidate).
        if (started.some((o) => o.kind === 'sync')) {
          dispatch(markActivationCompleted());
        }

        // Only surface the error toast when a retry genuinely failed to start.
        if (outcomes.some((outcome) => outcome.kind === 'error')) {
          showErrorToast(translateText(RETRY_ERROR_KEY));
        }
      });
    },
    [submit, dispatch, showErrorToast, track]
  );

  return { retry, isRetrying: retryingBatchIds.length > 0, retryingBatchIds };
};
